import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import SelectInput, { SelectDataType } from './SelectInput';
import StackedAreaGd from './StackedAreaGd';
import RangoTemporalSlider from './RangoTemporalSlider';
import { FuenteGd } from './TreemapGd';
import { construirColorPorTecnologia } from '../utils/estadisticasGdPalette';
import { getUncatchEndpointData } from '../utils/request';

const VALOR_TODAS = '__TODAS__';

const OPCION_TODAS_REGIONES: SelectDataType = {
  value: VALOR_TODAS,
  name: 'Todas las regiones'
};
const OPCION_TODAS_COMUNAS: SelectDataType = {
  value: VALOR_TODAS,
  name: 'Todas las comunas'
};

type PuntoSerie = {
  fecha: string;
  totalProyectos: number;
  [key: string]: string | number;
};

type EvolucionPayload = {
  fuente: FuenteGd;
  region: string | null;
  comuna: string | null;
  unidadCapacidad: 'MW' | 'kW';
  tecnologias: string[];
  rangoDisponible: { min: string | null; max: string | null };
  rangoAplicado: { desde: string; hasta: string };
  serie: PuntoSerie[];
};

const Bloque2BEvolucionGd: FunctionComponent<{
  fuente: FuenteGd;
  titulo: string;
}> = ({ fuente, titulo }) => {
  const [regiones, setRegiones] = useState<SelectDataType[]>([OPCION_TODAS_REGIONES]);
  const [comunas, setComunas] = useState<SelectDataType[]>([OPCION_TODAS_COMUNAS]);
  const [regionSel, setRegionSel] = useState<string>(VALOR_TODAS);
  const [comunaSel, setComunaSel] = useState<string>(VALOR_TODAS);

  const [payload, setPayload] = useState<EvolucionPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [desde, setDesde] = useState<string>('');
  const [hasta, setHasta] = useState<string>('');

  useEffect(() => {
    let cancelado = false;
    const fetchRegiones = async () => {
      try {
        const res = await getUncatchEndpointData({
          endpoint: 'estadisticas-gd/regiones',
          query: { fuente }
        });
        if (cancelado) return;
        const lista: string[] = (res && res.regiones) || [];
        setRegiones([OPCION_TODAS_REGIONES, ...lista.map((r) => ({ value: r, name: r }))]);
      } catch (err) {
        if (cancelado) return;
        console.error(err);
        setRegiones([OPCION_TODAS_REGIONES]);
      }
    };
    fetchRegiones();
    return () => {
      cancelado = true;
    };
  }, [fuente]);

  useEffect(() => {
    let cancelado = false;
    const fetchComunas = async () => {
      try {
        const query: Record<string, string> = { fuente };
        if (regionSel !== VALOR_TODAS) query.region = regionSel;
        const res = await getUncatchEndpointData({
          endpoint: 'estadisticas-gd/comunas',
          query
        });
        if (cancelado) return;
        const lista: string[] = (res && res.comunas) || [];
        setComunas([OPCION_TODAS_COMUNAS, ...lista.map((c) => ({ value: c, name: c }))]);
        setComunaSel(VALOR_TODAS);
      } catch (err) {
        if (cancelado) return;
        console.error(err);
        setComunas([OPCION_TODAS_COMUNAS]);
        setComunaSel(VALOR_TODAS);
      }
    };
    fetchComunas();
    return () => {
      cancelado = true;
    };
  }, [fuente, regionSel]);

  useEffect(() => {
    let cancelado = false;
    const fetchEvolucion = async () => {
      try {
        setLoading(true);
        setError(null);
        const query: Record<string, string> = { fuente };
        if (regionSel !== VALOR_TODAS) query.region = regionSel;
        if (comunaSel !== VALOR_TODAS) query.comuna = comunaSel;
        const res = (await getUncatchEndpointData({
          endpoint: 'estadisticas-gd/evolucion',
          query
        })) as EvolucionPayload;
        if (cancelado) return;
        setPayload(res);
        setDesde(res.rangoAplicado.desde);
        setHasta(res.rangoAplicado.hasta);
      } catch (err) {
        if (cancelado) return;
        console.error(err);
        setError('No se pudo obtener la evolución temporal.');
        setPayload(null);
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    fetchEvolucion();
    return () => {
      cancelado = true;
    };
  }, [fuente, regionSel, comunaSel]);

  const meses = useMemo(() => {
    if (!payload) return [];
    return payload.serie.map((p) => p.fecha);
  }, [payload]);

  const colorPorTecnologia = useMemo(() => {
    if (!payload) return new Map<string, string>();
    return construirColorPorTecnologia(payload.tecnologias);
  }, [payload]);

  if (loading && !payload) {
    return (
      <div className='estadisticas-gd-bloque2b-fuente'>
        <h3 className='estadisticas-gd-fuente-titulo'>{titulo}</h3>
        <div className='stacked-area-gd-status'>Cargando evolución temporal...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='estadisticas-gd-bloque2b-fuente'>
        <h3 className='estadisticas-gd-fuente-titulo'>{titulo}</h3>
        <div className='stacked-area-gd-status stacked-area-gd-status--error'>{error}</div>
      </div>
    );
  }

  if (!payload || meses.length === 0) {
    return (
      <div className='estadisticas-gd-bloque2b-fuente'>
        <h3 className='estadisticas-gd-fuente-titulo'>{titulo}</h3>
        <div className='stacked-area-gd-status'>
          No hay datos de evolución temporal para los filtros seleccionados.
        </div>
      </div>
    );
  }

  const desdeEfectivo = desde || meses[0];
  const hastaEfectivo = hasta || meses[meses.length - 1];

  return (
    <div className='estadisticas-gd-bloque2b-fuente'>
      <div className='estadisticas-gd-fuente-header'>
        <h3 className='estadisticas-gd-fuente-titulo'>{titulo}</h3>
        <div className='estadisticas-gd-fuente-filtros estadisticas-gd-fuente-filtros--doble'>
          <div className='estadisticas-gd-filtro'>
            <label className='estadisticas-gd-filtro-label'>Región</label>
            <SelectInput
              label='Selecciona Región'
              name={`region-${fuente}-evol`}
              id={`region-${fuente}-evol`}
              value={regionSel}
              onChange={(e) => setRegionSel(e.target.value)}
              options={regiones}
            />
          </div>
          <div className='estadisticas-gd-filtro'>
            <label className='estadisticas-gd-filtro-label'>Comuna</label>
            <SelectInput
              label='Selecciona Comuna'
              name={`comuna-${fuente}-evol`}
              id={`comuna-${fuente}-evol`}
              value={comunaSel}
              onChange={(e) => setComunaSel(e.target.value)}
              options={comunas}
            />
          </div>
        </div>
      </div>

      <div className='estadisticas-gd-evol-pair'>
        <div className='estadisticas-gd-evol-chart'>
          <h4 className='estadisticas-gd-evol-chart-titulo'>Cantidad acumulada de proyectos</h4>
          <StackedAreaGd
            serie={payload.serie}
            tecnologias={payload.tecnologias}
            colorPorTecnologia={colorPorTecnologia}
            unidadCapacidad={payload.unidadCapacidad}
            metrica='cantidad'
            desde={desdeEfectivo}
            hasta={hastaEfectivo}
          />
        </div>
        <div className='estadisticas-gd-evol-chart'>
          <h4 className='estadisticas-gd-evol-chart-titulo'>
            Capacidad acumulada ({payload.unidadCapacidad})
          </h4>
          <StackedAreaGd
            serie={payload.serie}
            tecnologias={payload.tecnologias}
            colorPorTecnologia={colorPorTecnologia}
            unidadCapacidad={payload.unidadCapacidad}
            metrica='capacidad'
            desde={desdeEfectivo}
            hasta={hastaEfectivo}
          />
        </div>
      </div>

      <RangoTemporalSlider
        meses={meses}
        desde={desdeEfectivo}
        hasta={hastaEfectivo}
        onChange={(d, h) => {
          setDesde(d);
          setHasta(h);
        }}
      />
    </div>
  );
};

export default Bloque2BEvolucionGd;
